import api from "./api";
import { Product } from "../types/product";
import { Tax } from "../types/tax";

interface AdminProduct {
  id: number | string;
  name: string;
  description?: string | null;
  barcode?: string | null;
  sku?: string | null;
  price?: number | string;
  discount?: number | string | null;
  discount_type?: string | null;
  discounted_price?: number | string;
  image?: string | null;
  default_image?: string | null;
  product_image_url?: string | null;
  status?: string | null;
  stock?: number;
  quantity?: number;
  location?: { name?: string | null; id?: number | string };
  location_id?: number | string;
  category?: { name?: string | null; id?: number | string };
  formated_price?: string;
  formated_discounted_price?: string;
  product_taxes?: Array<{ tax_id?: number | string; tax?: { id: number; name: string; percent: number } }>;
  [key: string]: unknown;
}

interface AdminProductsResponse {
  success: boolean;
  data: AdminProduct[];
  message?: string;
}

interface AdminProductResponse {
  success: boolean;
  data: AdminProduct;
  message?: string;
}

export interface ProductMutationPayload {
  company_id: number | string;
  location_id: number | string;
  name: string;
  price: number | string;
  description?: string;
  barcode?: string;
  sku?: string;
  discount?: number;
  discount_type?: "percent" | "amount";
  tax_ids?: number[];
}

const normalizeStatus = (
  apiStatus: string | null | undefined,
  stock: number
): Product["status"] => {
  if (stock <= 0) return "Out of Stock";
  if (stock < 10) return "Low Stock";

  if (apiStatus?.toLowerCase() === "inactive") {
    return "Out of Stock";
  }

  return "In Stock";
};

const mapAdminProductToProduct = (item: AdminProduct): Product => {
  // Use sku as quantity/stock - parse it as a number
  let stock = 0;
  if (item.sku) {
    const skuNum = typeof item.sku === "string" ? parseFloat(item.sku) : Number(item.sku);
    stock = !Number.isNaN(skuNum) ? skuNum : 0;
  } else if (typeof item.stock === "number") {
    stock = item.stock;
  } else if (typeof item.quantity === "number") {
    stock = item.quantity;
  }

  const images = [
    item.product_image_url,
    item.image,
    item.default_image,
  ].filter((src): src is string => Boolean(src));

  // Extract tax IDs from product_taxes array
  const taxIds: number[] = [];
  if (Array.isArray(item.product_taxes)) {
    item.product_taxes.forEach((pt) => {
      if (pt.tax_id) {
        const taxId = typeof pt.tax_id === "string" ? parseInt(pt.tax_id) : pt.tax_id;
        if (!Number.isNaN(taxId)) {
          taxIds.push(taxId);
        }
      }
    });
  }

  // Extract taxes
  const taxes: Array<{ id: number; name: string; percent: number }> = [];
  if (Array.isArray(item.product_taxes)) {
    item.product_taxes.forEach((pt: any) => {
      if (pt.tax) {
        taxes.push({
          id: pt.tax.id,
          name: pt.tax.name,
          percent: pt.tax.percent,
        });
      }
    });
  }

  // Extract discount info
  const discountType =
    item.discount_type?.toLowerCase() === "amount" ? "amount" : "percent";

  const discountValue =
    typeof item.discount === "string"
      ? parseFloat(item.discount)
      : item.discount ?? undefined;

  return {
    id: String(item.id),
    name: item.name || "Untitled product",
    brand: undefined,
    category: item.category?.name ?? item.location?.name ?? undefined,
    price: typeof item.price === "string" ? parseFloat(item.price) : item.price || 0,
    stock,
    status: normalizeStatus(item.status, stock),
    image: item.product_image_url || images[0],
    images: images.length > 0 ? images : undefined,
    sku: item.sku || undefined,
    barcode: item.barcode || undefined,
    discount: discountValue,
    discountType,
    discountedPrice:
      typeof item.discounted_price === "string"
        ? parseFloat(item.discounted_price)
        : item.discounted_price ?? undefined,
    description: item.description ?? undefined,
    taxIds: taxIds.length > 0 ? taxIds : undefined,
    taxes: taxes.length > 0 ? taxes : undefined,
    formattedPrice: item.formated_price as string,
    formattedDiscountedPrice: item.formated_discounted_price as string,
    locationName: item.location?.name as string,
    locationId:
      typeof item.location_id === "number"
        ? item.location_id
        : typeof item.location_id === "string"
          ? parseInt(item.location_id)
          : typeof item.location?.id === "number"
            ? item.location.id
            : typeof item.location?.id === "string"
              ? parseInt(item.location.id)
              : undefined,
    quantity: 1,
  };
};

export const getAdminProducts = async (): Promise<Product[]> => {
  const response = await api.get<AdminProductsResponse>("/admin/products");
  const payload = response.data;
  if (!payload?.success || !Array.isArray(payload.data)) {
    return [];
  }

  return payload.data.map(mapAdminProductToProduct);
};

export const getProductTaxes = async (): Promise<Tax[]> => {
  try {
    const response = await api.get<{
      success: boolean;
      taxes: Tax[];
      message?: string;
    }>("/admin/products/taxes");

    if (response.data.success && Array.isArray(response.data.taxes)) {
      return response.data.taxes;
    }
  } catch (error: any) {
    console.error("Failed to fetch product taxes:", error.response?.data || error.message);
  }

  return [];
};

const buildProductFormData = (
  payload: ProductMutationPayload,
  method: "POST" | "PUT" = "POST"
): FormData => {
  const formData = new FormData();

  if (method === "PUT") {
    formData.append("_method", "PUT");
  }

  formData.append("company_id", String(payload.company_id));
  formData.append("location_id", String(payload.location_id));
  formData.append("name", payload.name);
  formData.append("price", String(payload.price));

  if (payload.description) {
    formData.append("description", payload.description);
  }
  if (payload.barcode) {
    formData.append("barcode", payload.barcode);
  }
  if (payload.sku) {
    formData.append("sku", payload.sku);
  }
  if (payload.discount !== undefined) {
    formData.append("discount", String(payload.discount));
  }
  if (payload.discount_type) {
    formData.append("discount_type", payload.discount_type);
  }
  if (payload.tax_ids && payload.tax_ids.length > 0) {
    // Backend expects an array, so we must append each item with '[]'
    payload.tax_ids.forEach((taxId) => {
      formData.append("tax_ids[]", String(taxId));
    });
  }

  return formData;
};

export const createAdminProduct = async (
  payload: ProductMutationPayload
): Promise<Product> => {
  const formData = buildProductFormData(payload, "POST");
  // Don't set Content-Type header - let axios set it automatically with boundary
  const response = await api.post("/admin/products", formData);
  const created =
    (response.data?.data as AdminProduct) || (response.data as AdminProduct);
  return mapAdminProductToProduct(created);
};

export const updateAdminProduct = async (
  id: string,
  payload: ProductMutationPayload
): Promise<Product> => {
  const formData = buildProductFormData(payload, "PUT");
  // Don't set Content-Type header - let axios set it automatically with boundary
  const response = await api.post(`/admin/products/${id}`, formData);
  const updated =
    (response.data?.data as AdminProduct) || (response.data as AdminProduct);
  return mapAdminProductToProduct(updated);
};

export const storeProductImages = async (
  productId: string,
  imageFiles: File[],
  defaultImage?: string
): Promise<void> => {
  if (imageFiles.length === 0) return;

  const formData = new FormData();
  formData.append("product_id", productId);
  imageFiles.forEach((file) => {
    formData.append("file[]", file);
  });
  if (defaultImage) {
    formData.append("default_image", defaultImage);
  }

  await api.post("/admin/products/store-images", formData);
};

export const updateProductImages = async (
  productId: string,
  newImageFiles: File[],
  existingImageFilenames: string[],
  defaultImage?: string
): Promise<{ images: string[]; default_image: string | null } | void> => {
  const formData = new FormData();

  formData.append("product_id", productId);

  // Add new image files
  if (newImageFiles.length > 0) {
    newImageFiles.forEach((file) => {
      formData.append("file[]", file);
    });
  }

  // Always send uploaded_files to preserve existing images
  // If existingImageFilenames is empty, send empty array (user removed all)
  // If it has values, send them to keep those images
  formData.append("uploaded_files", JSON.stringify(existingImageFilenames));

  if (defaultImage) {
    formData.append("default_image", defaultImage);
  }

  const response = await api.post<{
    success: boolean;
    data: {
      images: string[];
      default_image: string | null;
    };
  }>("/admin/products/update-images", formData);

  if (response.data.success && response.data.data) {
    return response.data.data;
  }
};

export const deleteAdminProduct = async (id: string): Promise<void> => {
  await api.delete(`/admin/products/${id}`);
};

export const getProductById = async (id: string | number): Promise<Product | null> => {
  try {
    // Attempt 1: Fetch direct resource
    const response = await api.get<any>(`/admin/products/${id}`);
    const payload = response.data;

    if (payload) {
      // 1. { success: true, data: { ...product } }
      if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
        return mapAdminProductToProduct(payload.data);
      }
      // 2. { success: true, product: { ... } }
      if (payload.product && typeof payload.product === "object") {
        return mapAdminProductToProduct(payload.product);
      }
      // 3. Direct object
      if (payload.id || payload.name) {
        return mapAdminProductToProduct(payload);
      }
    }

    // Attempt 2: Fallback to list search
    const allProducts = await getAdminProducts();
    return allProducts.find((p) => String(p.id) === String(id)) || null;
  } catch (error: any) {
    console.warn(`Direct fetch for product ${id} failed, trying list fallback...`, error.message);

    // Attempt 3: Fallback on exception
    try {
      const allProducts = await getAdminProducts();
      return allProducts.find((p) => String(p.id) === String(id)) || null;
    } catch (fallbackError) {
      return null;
    }
  }
};
